import moment, { Moment } from 'moment'
import { IFeed } from 'models/IFeed'
import { IGroupApexChart } from 'models/IGroup'
import { CleaningScope } from './cleaningMetrics'
import { MetricBadge, MetricCardData, MetricPart } from './dayMetrics'

export type PushPoint = {
	date: string
	count: number | null
}

export type PsvPoint = {
	date: number
	psv: number | null
	heads: number
}

export type GroupGraph = {
	groupId: number
	points: IGroupApexChart[]
}

const value = (text: string): MetricPart => ({ text, tone: 'value' })
const unit = (text: string): MetricPart => ({ text, tone: 'unit' })
const muted = (text: string): MetricPart => ({ text, tone: 'muted' })
const prefix = (text: string): MetricPart => ({ text, tone: 'prefix' })

const formatInt = (amount: number) => Math.round(amount).toLocaleString('ru-RU')

const groupBadge = (title: string): MetricBadge => ({
	text: title,
	tone: 'neutral',
})

const alertBadge = (text: string): MetricBadge => ({
	text,
	tone: 'alert',
})

const formatMetric = (amount: number) => {
	const rounded = Math.round(amount * 10) / 10
	return rounded.toLocaleString('ru-RU', { maximumFractionDigits: 1 })
}

const toMoment = (stamp?: number | null) => {
	if (stamp == null) return null
	return stamp < 1e12 ? moment.unix(stamp) : moment(stamp)
}

const inScope = (groupId: number | undefined, scope: CleaningScope) =>
	groupId != null &&
	(scope.groupIds.length === 0 || scope.groupIds.includes(groupId))

type NudgeHit = {
	groupId: number
	groupTitle: string
	date: string
	at: Moment
}

const collectNudges = (
	days: { day: string; plans: IFeed[] }[],
	scope: CleaningScope,
) => {
	const hits: NudgeHit[] = []
	days.forEach(item => {
		item.plans.forEach(plan => {
			if (plan.type !== 'nudge') return
			plan.feed_facts?.forEach(fact => {
				const groupId = fact.group?.id
				if (!inScope(groupId, scope) || groupId == null || !fact.fact_dt) return
				const at = toMoment(fact.fact_dt)
				if (!at) return
				hits.push({
					groupId,
					groupTitle: fact.group?.title || '',
					date: item.day,
					at,
				})
			})
		})
	})
	return hits
}

const isDayHour = (hour: number) => hour >= 6 && hour < 21

const averageDecline = (hits: NudgeHit[], graphs: GroupGraph[]) => {
	const byGroup = new Map(
		graphs.map(graph => [
			graph.groupId,
			graph.points
				.filter(point => point.start_dt != null)
				.slice()
				.sort((left, right) => left.start_dt - right.start_dt),
		]),
	)
	const declines: number[] = []

	hits.forEach(hit => {
		const points = byGroup.get(hit.groupId)
		if (!points?.length) return
		let before: IGroupApexChart | null = null
		let after: IGroupApexChart | null = null
		points.forEach(point => {
			const at = toMoment(point.start_dt)
			if (!at) return
			if (!at.isAfter(hit.at)) before = point
			if (at.isAfter(hit.at) && !after) after = point
		})
		const beforeFeed = before?.ur_feed
		const afterFeed = after?.ur_feed
		if (beforeFeed == null || afterFeed == null || beforeFeed <= 0) return
		declines.push(((beforeFeed - afterFeed) / beforeFeed) * 100)
	})

	if (!declines.length) return null
	return declines.reduce((sum, item) => sum + item, 0) / declines.length
}

export const buildNudgeCards = (
	days: { day: string; plans: IFeed[] }[],
	scope: CleaningScope,
	groups: { id: number; title: string }[],
	graphs: GroupGraph[],
): { cards: MetricCardData[]; trend: PushPoint[] } => {
	const hits = collectNudges(days, scope)
	const scoped = groups.filter(group => inScope(group.id, scope))
	const counts = new Map<
		string,
		{ groupId: number; title: string; date: string; count: number }
	>()

	days.forEach(item => {
		scoped.forEach(group => {
			counts.set(`${group.id}|${item.day}`, {
				groupId: group.id,
				title: group.title,
				date: item.day,
				count: 0,
			})
		})
	})
	hits.forEach(hit => {
		const key = `${hit.groupId}|${hit.date}`
		const current = counts.get(key)
		if (current) current.count += 1
	})

	const samples = Array.from(counts.values())
	const minSample = samples.reduce<(typeof samples)[number] | null>(
		(best, item) => (!best || item.count < best.count ? item : best),
		null,
	)
	const maxSample = samples.reduce<(typeof samples)[number] | null>(
		(best, item) => (!best || item.count > best.count ? item : best),
		null,
	)

	const totals = new Map<number, number>()
	scoped.forEach(group => totals.set(group.id, 0))
	hits.forEach(hit => {
		totals.set(hit.groupId, (totals.get(hit.groupId) ?? 0) + 1)
	})
	const avgPushes =
		totals.size > 0
			? Array.from(totals.values()).reduce((sum, item) => sum + item, 0) /
				totals.size
			: null

	let dayCount = 0
	let nightCount = 0
	hits.forEach(hit => {
		if (isDayHour(hit.at.hour())) dayCount += 1
		else nightCount += 1
	})
	const dayAverage = days.length ? dayCount / days.length : null
	const nightAverage = days.length ? nightCount / days.length : null
	const decline = averageDecline(hits, graphs)
	const showDetails = scope.period || scope.multipleGroups
	const cards: MetricCardData[] = []

	if (showDetails) {
		cards.push({
			id: 'nudge-min',
			parts: [value(formatInt(minSample?.count ?? 0)), unit('раз/день')],
			label: 'Минимальное количество подталкиваний',
			empty: !minSample,
			badge:
				scope.multipleGroups && minSample
					? groupBadge(minSample.title)
					: undefined,
			aside:
				scope.period && minSample
					? `было ${moment(minSample.date).format('DD.MM.YY')}`
					: undefined,
		})
		cards.push({
			id: 'nudge-max',
			parts: [value(formatInt(maxSample?.count ?? 0)), unit('раз/день')],
			label: 'Максимальное количество подталкиваний',
			empty: !maxSample,
			badge:
				scope.multipleGroups && maxSample
					? groupBadge(maxSample.title)
					: undefined,
			aside:
				scope.period && maxSample
					? `было ${moment(maxSample.date).format('DD.MM.YY')}`
					: undefined,
		})
		cards.push({
			id: 'nudge-avg',
			parts: [value(formatInt(avgPushes ?? 0)), unit('раз/день')],
			label: 'Среднее количество подталкиваний',
			empty: avgPushes == null,
		})
	}

	cards.push({
		id: 'nudge-drop',
		parts: [prefix('на'), value(formatInt(decline ?? 0)), unit('%')],
		label: 'Средний спад недоступного корма после подталкивания',
		empty: decline == null,
	})
	cards.push({
		id: 'nudge-day',
		parts: [value(formatInt(dayAverage ?? 0)), unit('раз/день')],
		label: 'Среднее количество подталкиваний за день',
		aside: 'с 06:00-21:00',
		empty: !hits.length,
	})
	cards.push({
		id: 'nudge-night',
		parts: [value(formatInt(nightAverage ?? 0)), unit('раз/день')],
		label: 'Среднее количество подталкиваний за ночь',
		aside: 'с 21:00-06:00',
		empty: !hits.length,
	})

	const byDay = new Map<string, number>()
	hits.forEach(hit => byDay.set(hit.date, (byDay.get(hit.date) ?? 0) + 1))
	const trend = days.map(item => {
		const planned = item.plans.some(
			plan =>
				plan.type === 'nudge' &&
				plan.feed_facts?.some(fact => inScope(fact.group?.id, scope)),
		)
		const count = byDay.get(item.day) ?? 0
		return {
			date: item.day,
			count: planned || count > 0 ? count : null,
		}
	})

	return { cards, trend }
}

export type FeedLocatedNumber = {
	value: number
	group_id: number | null
	date: string | null
}

export type FeedNudgeMetrics = {
	scheduledValue: number | null
	scheduledQuality: number | null
	decline: number | null
	pushesAm: number | null
	pushesNight: number | null
	minPushes: FeedLocatedNumber | null
	maxPushes: FeedLocatedNumber | null
	avgPushes: number | null
}

const countCard = (
	id: string,
	label: string,
	amount: number | null,
	extra?: { badge?: MetricBadge; aside?: string },
): MetricCardData => ({
	id,
	parts: [value(amount == null ? '0' : formatMetric(amount)), unit('раз/день')],
	label,
	empty: amount == null,
	badge: extra?.badge,
	aside: extra?.aside,
})

const locatedBadge = (
	item: FeedLocatedNumber | null,
	multipleGroups: boolean,
	groups: { id: number; title: string }[],
) => {
	if (!multipleGroups || item?.group_id == null) return undefined
	const title = groups.find(group => group.id === item.group_id)?.title
	return title ? groupBadge(title) : undefined
}

const locatedAside = (item: FeedLocatedNumber | null, period: boolean) =>
	period && item?.date
		? `было ${moment(item.date).format('DD.MM.YY')}`
		: undefined

export const buildFeedNudgeCards = (
	metrics: FeedNudgeMetrics | null,
	scope: CleaningScope,
	groups: { id: number; title: string }[],
): { pushesCard: MetricCardData; cards: MetricCardData[] } => {
	const scheduled = metrics?.scheduledValue ?? null
	const quality = metrics?.scheduledQuality ?? null
	const cards: MetricCardData[] = []
	const showDetails = scope.period || scope.multipleGroups

	if (showDetails) {
		const minPushes = metrics?.minPushes ?? null
		const maxPushes = metrics?.maxPushes ?? null
		cards.push(
			countCard(
				'nudge-min',
				'Минимальное количество подталкиваний',
				minPushes?.value ?? null,
				{
					badge: locatedBadge(minPushes, scope.multipleGroups, groups),
					aside: locatedAside(minPushes, scope.period),
				},
			),
		)
		cards.push(
			countCard(
				'nudge-max',
				'Максимальное количество подталкиваний',
				maxPushes?.value ?? null,
				{
					badge: locatedBadge(maxPushes, scope.multipleGroups, groups),
					aside: locatedAside(maxPushes, scope.period),
				},
			),
		)
		cards.push(
			countCard(
				'nudge-avg',
				'Среднее количество подталкиваний',
				metrics?.avgPushes ?? null,
			),
		)
	}

	const decline = metrics?.decline ?? null
	cards.push({
		id: 'nudge-drop',
		parts: [
			prefix('на'),
			value(decline == null ? '0' : formatMetric(decline)),
			unit('%'),
		],
		label: 'Средний спад недоступного корма после подталкивания',
		empty: decline == null,
	})
	cards.push({
		...countCard(
			'nudge-day',
			'Среднее количество подталкиваний за день',
			metrics?.pushesAm ?? null,
		),
		aside: 'с 06:00-21:00',
	})
	cards.push({
		...countCard(
			'nudge-night',
			'Среднее количество подталкиваний за ночь',
			metrics?.pushesNight ?? null,
		),
		aside: 'с 21:00-06:00',
	})

	return {
		pushesCard: {
			id: 'nudge-schedule',
			parts: [
				value(scheduled == null ? '0' : formatMetric(scheduled)),
				unit('%'),
			],
			label: 'Выполнено подталкиваний по расписанию',
			empty: scheduled == null,
			badge:
				quality == null
					? undefined
					: alertBadge(`${formatMetric(quality)}% качественно`),
		},
		cards,
	}
}

export const buildPsvCard = (
	fact: number | null,
	norm: number | null,
): MetricCardData => ({
	id: 'psv-avg',
	parts: [
		value(formatInt(fact ?? 0)),
		unit('кг/гол'),
		muted(`/ ${norm == null ? '—' : formatInt(norm)} кг/гол`),
	],
	label: 'Среднее ПСВ',
	labelNote: '(факт/план)',
	empty: fact == null,
})
